const hasValue = (value) =>
  value !== undefined && value !== null && value !== "" &&
  (!Array.isArray(value) || value.length > 0);

const numeric = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const unwrapChoiceValue = (val) => {
  if (val !== null && typeof val === "object" && !Array.isArray(val)) {
    if (val.value !== undefined && val.value !== null) return val.value;
    if (val.code !== undefined && val.code !== null) return val.code;
  }
  return val;
};

const equal = (left, right) => {
  const normLeft = unwrapChoiceValue(left);
  const normRight = unwrapChoiceValue(right);
  if (typeof normLeft === "string" && typeof normRight === "string") {
    return normLeft.trim().toLowerCase() === normRight.trim().toLowerCase();
  }
  return normLeft === normRight;
};

const compare = (actual, operator, expected) => {
  const rawOperator = String(operator || "eq").toLowerCase();
  const normalizedOperator = (
    TREATMENT_RULE_OPERATOR_ALIASES[rawOperator] || rawOperator
  );
  const normalizedActual = Array.isArray(actual)
    ? actual.map(unwrapChoiceValue)
    : unwrapChoiceValue(actual);
  const normalizedExpected = Array.isArray(expected)
    ? expected.map(unwrapChoiceValue)
    : unwrapChoiceValue(expected);

  switch (normalizedOperator) {
    case "eq":
    case "equals":
      return Array.isArray(normalizedActual)
        ? normalizedActual.some((value) => equal(value, normalizedExpected))
        : equal(normalizedActual, normalizedExpected);
    case "neq":
    case "not_equals":
      return Array.isArray(normalizedActual)
        ? !normalizedActual.some((value) => equal(value, normalizedExpected))
        : !equal(normalizedActual, normalizedExpected);
    case "contains":
      return Array.isArray(normalizedActual)
        ? normalizedActual.map(String).includes(String(normalizedExpected))
        : typeof normalizedActual === "string" && normalizedActual.includes(String(normalizedExpected));
    case "not_contains":
      return !compare(normalizedActual, "contains", normalizedExpected);
    case "in": {
      const options = Array.isArray(normalizedExpected)
        ? normalizedExpected
        : String(normalizedExpected || "").split(",").map((item) => item.trim());
      return options.some((value) => equal(normalizedActual, value));
    }
    case "not_in":
      return !compare(normalizedActual, "in", normalizedExpected);
    case "exists":
      return hasValue(normalizedActual) === (normalizedExpected === undefined ? true : Boolean(normalizedExpected));
    case "is_empty":
      return !hasValue(normalizedActual);
    case "is_not_empty":
      return hasValue(normalizedActual);
    case "between": {
      const range = Array.isArray(normalizedExpected)
        ? normalizedExpected
        : String(normalizedExpected || "").split(",").map((item) => item.trim());
      const [number, low, high] = [numeric(normalizedActual), numeric(range[0]), numeric(range[1])];
      return number !== null && low !== null && high !== null && number >= Math.min(low, high) && number <= Math.max(low, high);
    }
    default: {
      const left = numeric(normalizedActual);
      const right = numeric(normalizedExpected);
      if (left === null || right === null) return false;
      if (normalizedOperator === "gt") return left > right;
      if (normalizedOperator === "gte") return left >= right;
      if (normalizedOperator === "lt") return left < right;
      if (normalizedOperator === "lte") return left <= right;
      return false;
    }
  }
};

export const evaluateRule = (rule, context) => {
  if (rule == null) return true;
  if (typeof rule !== "object" || Array.isArray(rule)) return false;
  if (!Object.keys(rule).length) return true;

  const wrapperKeys = ["show_if", "skip_if"].filter((key) =>
    Object.prototype.hasOwnProperty.call(rule, key),
  );
  if (wrapperKeys.length) {
    const malformedWrapper = wrapperKeys.some((key) => {
      const value = rule[key];
      return (
        value == null
        || typeof value !== "object"
        || Array.isArray(value)
        || !Object.keys(value).length
      );
    });
    if (malformedWrapper) return false;
    if (wrapperKeys.includes("skip_if") && evaluateRule(rule.skip_if, context)) {
      return false;
    }
    if (wrapperKeys.includes("show_if")) return evaluateRule(rule.show_if, context);
    return false;
  }

  const CHILD_KEYS = ["rules", "conditions", "subgroups", "children"];
  const declaresChildren = CHILD_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(rule, key),
  );
  if (declaresChildren && CHILD_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(rule, key) && !Array.isArray(rule[key]),
  )) return false;
  const children = CHILD_KEYS.flatMap(
    (key) => (Array.isArray(rule[key]) ? rule[key] : []),
  );
  // A group that declares an empty child list has no conditions, so it
  // matches -- mirroring RuleEvaluator._children/evaluate on the server.
  // { combinator: "and", rules: [] } is what the admin matching editor saves
  // for "no answer conditions"; falling through to field evaluation would
  // return false and silently hide the treatment.
  if (declaresChildren && !children.length) {
    // The server accepts an explicitly empty group before inspecting its
    // combinator. Preserve that legacy compatibility: an empty group means
    // the admin removed every condition and therefore imposes no constraint.
    return true;
  }
  if (children.length) {
    const group = String(
      rule.combinator || rule.logic || rule.operator ||
      (rule.mode === "simple" ? "or" : "and")
    ).toLowerCase();
    if (group !== "and" && group !== "or") return false;
    return group === "or"
      ? children.some((child) => evaluateRule(child, context))
      : children.every((child) => evaluateRule(child, context));
  }

  const source = String(rule.source || "answer").trim().toLowerCase();
  const sourceField = rule.field || rule.question_id || rule.questionId || "";
  if (source === "patient_profile" && !["sex", "age"].includes(String(sourceField))) {
    return false;
  }
  if (source !== "answer" && source !== "patient_profile") return false;
  const field = source === "patient_profile"
    ? `patient_profile.${sourceField}`
    : sourceField;
  // A non-empty rule without a field is malformed, not an unconditional rule.
  // Only an explicitly empty object or an explicitly empty group is
  // unconditional.
  if (!field || !rule.operator) return false;
  const operator = String(rule.operator).toLowerCase();
  if (![
    "eq", "equals", "neq", "not_equals", "contains", "not_contains",
    "in", "not_in", "exists", "is_empty", "is_not_empty", "between",
    "gt", "gte", "lt", "lte", "greater_than", "greater_than_or_equal",
    "less_than", "less_than_or_equal",
  ].includes(operator)) return false;
  if (
    !["exists", "is_empty", "is_not_empty"].includes(operator)
    && (
      !Object.prototype.hasOwnProperty.call(rule, "value")
      || rule.value == null
      || rule.value === ""
      || (Array.isArray(rule.value) && rule.value.length === 0)
    )
  ) return false;

  if (field === "__derived_bmi__" || rule.question_type === "bmi") {
    return compare(context.bmi, operator, rule.value);
  }

  if (
    !Object.prototype.hasOwnProperty.call(context, field)
    && !["exists", "is_empty", "is_not_empty"].includes(operator)
  ) {
    return false;
  }
  return compare(context[field], operator, rule.value);
};

export const meetsEligibility = (eligibility, context) => {
  const rules = eligibility || {};
  const age = numeric(context.age);
  const bmi = numeric(context.bmi);
  const sex = String(context.sex || context.gender || "").toLowerCase();
  if (rules.min_age != null && (age == null || age < Number(rules.min_age))) return false;
  if (rules.max_age != null && (age == null || age > Number(rules.max_age))) return false;
  if (rules.min_bmi != null && (bmi == null || bmi < Number(rules.min_bmi))) return false;
  if (rules.max_bmi != null && (bmi == null || bmi > Number(rules.max_bmi))) return false;
  if (rules.sex && String(rules.sex).toLowerCase() !== "any" && sex !== String(rules.sex).toLowerCase()) {
    return false;
  }
  return true;
};
import {
  TREATMENT_RULE_OPERATOR_ALIASES,
} from "./treatmentApiConstants";
